"use client";

import React, { Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { FileText, Type, Hash, Link, Code, Search, FileCode, GitCompare, Lock } from "lucide-react";

// Import individual tool components
import Base64Tool from "./components/Base64Tool";
import URLEncodeTool from "./components/URLEncodeTool";
import TextCaseTool from "./components/TextCaseTool";
import WordCounterTool from "./components/WordCounterTool";
import JSONFormatterTool from "./components/JSONFormatterTool";
import MarkdownTool from "./components/MarkdownTool";
import RegexTesterTool from "./components/RegexTesterTool";
import DiffCheckerTool from "./components/DiffCheckerTool";
import TextEncryptTool from "./components/TextEncryptTool";

const TABS = ["base64", "url-encode", "text-case", "word-counter", "json", "markdown", "regex", "diff", "encrypt"] as const;
type TabValue = (typeof TABS)[number];

const OVERVIEW_ITEMS: { id: TabValue; icon: React.ElementType; title: string; description: string }[] = [
  { id: "base64", icon: Hash, title: "Base64", description: "Encode/decode text and files" },
  { id: "url-encode", icon: Link, title: "URL Encode", description: "Encode/decode URLs" },
  { id: "text-case", icon: Type, title: "Case Converter", description: "Convert text cases" },
  { id: "word-counter", icon: FileText, title: "Word Counter", description: "Analyze text statistics" },
  { id: "json", icon: Code, title: "JSON Formatter", description: "Format and validate JSON" },
  { id: "markdown", icon: FileCode, title: "Markdown", description: "Preview and edit markdown" },
  { id: "regex", icon: Search, title: "Regex Tester", description: "Test regular expressions" },
  { id: "diff", icon: GitCompare, title: "Diff Checker", description: "Compare text differences" },
  { id: "encrypt", icon: Lock, title: "Text Encrypt", description: "Encrypt with custom alphabet" },
];

function TextToolsPageContent() {
  const [activeTab, setActiveTab] = useQueryParam<TabValue>("tab", {
    parse: (raw) => (raw ?? "base64") as TabValue,
    serialize: (v) => v ?? null,
    defaultValue: "base64",
    replace: true,
  });

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Text Tools</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Comprehensive text processing and manipulation tools
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="space-y-6">
          <TabsList className="flex w-full flex-wrap gap-2 p-2 rounded-xl bg-muted/60 h-auto min-h-12 [&>button]:shrink-0 [&>button]:rounded-lg [&>button]:data-[state=active]:bg-background [&>button]:data-[state=active]:shadow-sm">
            {OVERVIEW_ITEMS.map(({ id, icon: Icon, title }) => (
              <TabsTrigger key={id} value={id} className="flex items-center gap-2 px-4 py-2">
                <Icon className="h-4 w-4 shrink-0" />
                <span>{title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-h-[320px]">
            <TabsContent value="base64" className="mt-0 animate-in fade-in-0 duration-200">
              <Base64Tool />
            </TabsContent>
            <TabsContent value="url-encode" className="mt-0 animate-in fade-in-0 duration-200">
              <URLEncodeTool />
            </TabsContent>
            <TabsContent value="text-case" className="mt-0 animate-in fade-in-0 duration-200">
              <TextCaseTool />
            </TabsContent>
            <TabsContent value="word-counter" className="mt-0 animate-in fade-in-0 duration-200">
              <WordCounterTool />
            </TabsContent>
            <TabsContent value="json" className="mt-0 animate-in fade-in-0 duration-200">
              <JSONFormatterTool />
            </TabsContent>
            <TabsContent value="markdown" className="mt-0 animate-in fade-in-0 duration-200">
              <MarkdownTool />
            </TabsContent>
            <TabsContent value="regex" className="mt-0 animate-in fade-in-0 duration-200">
              <RegexTesterTool />
            </TabsContent>
            <TabsContent value="diff" className="mt-0 animate-in fade-in-0 duration-200">
              <DiffCheckerTool />
            </TabsContent>
            <TabsContent value="encrypt" className="mt-0 animate-in fade-in-0 duration-200">
              <TextEncryptTool />
            </TabsContent>
          </div>
        </Tabs>

        <Card className="card-enhanced mt-8 rounded-xl overflow-hidden">
          <CardHeader>
            <CardTitle>All Text Tools</CardTitle>
            <CardDescription>
              Click a card to switch to that tool
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {OVERVIEW_ITEMS.map(({ id, icon: Icon, title, description }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`text-left p-4 rounded-xl border transition-all duration-200 flex flex-col items-center sm:items-start gap-3 hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    activeTab === id
                      ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                    style={{ color: activeTab === id ? "#2563eb" : undefined }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm">{title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function TextToolsPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8"><div className="text-center">Loading...</div></div>}>
      <TextToolsPageContent />
    </Suspense>
  );
}
